import ForgotPassword from "./forgot-password";
import { LoginPropsCommon } from "./typings";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import React, { FormEvent } from "react";
import { classNames } from "~/utils";
import { LoadingSpinner } from "../icons";
import { Form, useActionData, useSearchParams } from "react-router";
import { Button, Input, toast } from "@mindstonehq/ui";
import * as jose from "jose";

interface LoginEmailProps extends LoginPropsCommon {
  redirectUrl: string;
  success: boolean;
  error: boolean;
  submitForgot: (e: FormEvent, email: string) => Promise<boolean>;
  resendPIN: (e: FormEvent, email: string) => Promise<boolean>;
  submitReset: (
    e: FormEvent,
    email: string,
    pin: string,
    password: string,
  ) => Promise<boolean>;
  validatePin: (e: FormEvent, email: string, pin: string) => Promise<boolean>;
}

export function LoginEmail({
  routerUtils,
  appRoutes,
  redirectUrl,
  success,
  error: urlError,
  submitForgot,
  resendPIN,
  submitReset,
  validatePin,
}: LoginEmailProps) {
  const { Link, useLocation } = routerUtils;
  const { t } = useLingui();
  const location = useLocation();
  const actionData = useActionData<any>();
  const [params] = useSearchParams();
  const inviteCode = React.useMemo(
    () => params.get("inviteCode") || "",
    [params],
  );
  const [email, setEmail] = React.useState("");
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [pin, setPin] = React.useState("");
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [uiState, setUIState] = React.useState<
    | "options"
    | "linkSent"
    | "forgot"
    | "validate-forgot-password"
    | "link"
    | "enter-pin"
  >("options");
  const emailRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const isOnlineEventRedirect =
    searchParams.get("onlineEventRedirect") === "true";
  const isCommunityEvent = searchParams.get("eventFlow") === "community";
  searchParams.delete("error");

  const linkSearchParams = React.useMemo(() => {
    const sp = new URLSearchParams(searchParams);
    if (email) sp.set("email", email);
    else sp.delete("email");
    return sp.toString();
  }, [searchParams, email]);

  const safeSearchParams = React.useMemo(() => {
    const safe = new URLSearchParams();
    const allowedParams = [
      "redirectUrl",
      "inviteCode",
      "onlineEventRedirect",
      "eventFlow",
      "meetupIntent",
    ];
    allowedParams.forEach((key) => {
      const value = searchParams.get(key);
      if (value) safe.set(key, value);
    });
    return safe.toString();
  }, [searchParams]);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (success) {
      setPassword("");
      setUIState("enter-pin");
      setLoading(false);
    }
  }, [success]);

  React.useEffect(() => {
    if (urlError) {
      setLoading(false);
    }
    setError(!!urlError);
  }, [urlError]);

  React.useEffect(() => {
    setLoading(false);
    if (actionData?.error) {
      setError(!!actionData.error);
      setUIState((current) => current === "enter-pin" ? current : "options");
    } else if (actionData?.pinRequested) {
      setError(false);
      setUIState("enter-pin");
      if (actionData?.email && !email) {
        setEmail(actionData.email);
      }
    } else {
      setError(false);
    }
  }, [actionData]);

  React.useEffect(() => {
    setTimeout(() => {
      switch (uiState) {
        case "options":
          emailRef.current?.focus();
          break;
      }
    }, 700);
  }, [uiState]);

  React.useEffect(() => {
    if (!inviteCode) return;
    try {
      const claims = jose.decodeJwt(inviteCode);
      const metadata = claims.Metadata
        ? JSON.parse(
            new TextDecoder().decode(
              jose.base64url.decode(claims.Metadata as string),
            ),
          )
        : null;
      if (metadata?.email) {
        setEmail(metadata.email);
        setInviteEmail(metadata.email);
      }
    } catch (error) {
      console.error("Failed to decode email from invite code:", error);
    }
  }, [inviteCode]);

  // Prefill email when returning from PIN request redirect
  React.useEffect(() => {
    const emailParam = params.get("email");
    if (emailParam && !email) {
      setEmail(emailParam);
    }
  }, [params]);

  const invalidInviteEmail = React.useMemo(
    () =>
      inviteCode &&
      inviteEmail !== "" &&
      email.toLocaleLowerCase() !== inviteEmail.toLowerCase(),
    [inviteCode, inviteEmail, email],
  );
  const renderGoBack = () => {
    if (uiState === "forgot" || uiState === "linkSent") return null;
    return (
      <div className="w-full flex justify-start relative">
        {uiState === "options" ? null : (
          <button
            className="flex items-center absolute left-0 -top-4"
            onClick={() => {
              setUIState("options");
            }}
          >
            <ArrowLeftIcon className="h-6 w-6 mr-1" />
          </button>
        )}
      </div>
    );
  };

  const handleSubmitForgot = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      await submitForgot(e, email);
      setLoading(false);
      setUIState("validate-forgot-password");
    } catch (e) {
      toast.error(t`Error sending reset code`, {
        description: t`Please refresh and try again`,
      });
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-[rgba(0,0,0,0.1)] text-lg">
          <LoadingSpinner />
        </div>
      )}
      {renderGoBack()}
      {uiState === "options" && (
        <>
          <div className="flex flex-col grow justify-center items-center w-full max-w-[400px]">
            <Form
              method="POST"
              action={`/login?index${safeSearchParams ? `&${safeSearchParams}` : ""}`}
              target="_self"
              className="w-full flex flex-col"
              onSubmit={() => {
                setLoading(true);
              }}
            >
              <input
                type="hidden"
                name="state"
                id={invalidInviteEmail ? "" : redirectUrl}
                value={invalidInviteEmail ? "" : redirectUrl}
              />
              <input type="hidden" name="inviteCode" value={inviteCode} />

              {/* Form Fields Section */}
              <div className="flex flex-col gap-2 w-full">
                {/* Email Input */}
                <div className="flex flex-col gap-1.5 w-full">
                  <label
                    htmlFor="email"
                    className="text-sm font-semibold leading-5 text-foreground"
                  >
                    <Trans>Email</Trans>
                  </label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t`Type your email`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (error) setError(false);
                      if (e.code === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    ref={emailRef}
                    className={classNames(
                      "h-10 placeholder:text-placeholder-foreground",
                      error ? "border-destructive" : "",
                    )}
                  />
                </div>

                {invalidInviteEmail && (
                  <div className="text-sm text-destructive">
                    <Trans>
                      The email does not match the invited email. The invite will
                      therefore not be accepted.{" "}
                      <a
                        href="mailto:support@mindstone.com"
                        className="text-info hover:text-info-hover cursor-pointer"
                      >
                        Contact support
                      </a>
                    </Trans>
                  </div>
                )}

                {/* Password Input */}
                <div className="flex flex-col gap-1.5 w-full">
                  <label
                    htmlFor="password"
                    className="text-sm font-semibold leading-5 text-foreground"
                  >
                    <Trans>Password</Trans>
                  </label>
                  <div className="relative">
                    <Input
                      type={passwordVisible ? "text" : "password"}
                      name="password"
                      id="password"
                      placeholder={t`Type your password`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      ref={passwordRef}
                      className="h-10 pr-10 placeholder:text-placeholder-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      aria-label={passwordVisible ? t`Hide password` : t`Show password`}
                      aria-pressed={passwordVisible}
                      aria-controls="password"
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {passwordVisible ? (
                        <EyeIcon className="w-5 h-5 text-placeholder-foreground" />
                      ) : (
                        <EyeSlashIcon className="w-5 h-5 text-placeholder-foreground" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs leading-5 text-placeholder-foreground">
                    <Trans>
                      <button
                        type="button"
                        className="underline hover:text-foreground"
                        onClick={() => setUIState("forgot")}
                      >
                        Forgot password?
                      </button>{" "}
                      or prefer to use a{" "}
                      <button
                        type="button"
                        className="underline hover:text-foreground"
                        onClick={() => setUIState("link")}
                      >
                        one-time login code
                      </button>
                      ?
                    </Trans>
                  </p>
                </div>
              </div>

              {isOnlineEventRedirect && isCommunityEvent && (
                <div className="text-xs text-left text-muted-foreground mt-2">
                  <Trans>You'll be checked in to the event as soon as you sign in.</Trans>
                </div>
              )}

              {isOnlineEventRedirect && !isCommunityEvent && (
                <div className="text-xs text-left text-warning-hover mt-2 flex flex-col gap-px">
                  <Trans>To join the live session, please ensure:</Trans>
                  <div className="pl-1">
                    <Trans>1. You've received an invitation to the program</Trans>
                  </div>
                  <div className="pl-1">
                    <Trans>2. Your signup email is the same as the one that you were
                    invited with</Trans>
                  </div>
                  <div>
                    <Trans>
                      Not received an invite? Contact help{" "}
                      <a
                        href="mailto:support@mindstone.com"
                        className="text-info hover:text-info-hover cursor-pointer"
                      >
                        here
                      </a>{" "}
                      or reach out to your admin
                    </Trans>
                  </div>
                </div>
              )}

              {error && (
                <p
                  className="text-sm text-destructive text-left mt-2"
                  id="signup-error"
                >
                  <Trans>Wrong credentials. Please check and try again.</Trans>
                </p>
              )}

              {/* Actions Section */}
              <div className="flex flex-col gap-2 w-full mt-6">
                {/* Sign In Button */}
                <Button
                  type="submit"
                  className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
                  disabled={!password || !email}
                  id="continue-pwd"
                >
                  <Trans>Sign in</Trans>
                </Button>

                {/* Sign Up Link */}
                <div className="flex items-center justify-center gap-1 text-xs leading-5">
                  <span className="text-theme-text-primary">
                    <Trans>Don't have an account?</Trans>
                  </span>
                  <Link
                    to={{
                      pathname: appRoutes.Signup,
                      search: linkSearchParams,
                    }}
                    className="text-primary-hover hover:underline"
                  >
                    <Trans>Sign up</Trans>
                  </Link>
                </div>
              </div>
            </Form>
          </div>
        </>
      )}

      {uiState === "linkSent" && (
        <div className="absolute inset-0 bg-background flex items-center justify-center z-50">
          {/* Back Button - Absolutely positioned top-left */}
          <button
            className="absolute top-6 left-8 flex items-center text-placeholder-foreground hover:text-foreground transition-colors z-10"
            onClick={() => setUIState("options")}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            <span className="text-sm"><Trans>Back</Trans></span>
          </button>

          <div className="flex flex-col justify-center items-center w-full max-w-[400px] gap-10 px-8">
            {/* Header */}
            <div className="text-center flex flex-col gap-2">
              <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground">
                <Trans>Check your email</Trans>
              </h2>
              <p className="text-base leading-7 text-muted-foreground">
                <Trans>We have sent you a one-time code to sign in without a password</Trans>
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-6 w-full text-center">
              <div className="text-sm text-muted-foreground">
                <div className="mb-1"><Trans>You did not receive the email?</Trans></div>
                <Form
                  method="POST"
                  action={`/login?index${safeSearchParams ? `&${safeSearchParams}` : ""}`}
                  target="_self"
                  className="inline"
                >
                  <input
                    type="hidden"
                    name="intent"
                    value="request-login-pin"
                  />
                  <input type="hidden" name="state" value={redirectUrl} />
                  <input type="hidden" name="email" value={email} />
                  <input type="hidden" name="inviteCode" value={inviteCode} />
                  <span><Trans>Check your spam folder or </Trans></span>
                  <button
                    type="submit"
                    className="text-primary-hover underline hover:no-underline font-semibold"
                    onClick={() => {
                      toast.success(t`Code resent`);
                    }}
                  >
                    <Trans>try again</Trans>
                  </button>
                </Form>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  className="text-sm text-muted-foreground hover:text-foreground underline cursor-pointer"
                  onClick={() => setUIState("enter-pin")}
                >
                  <Trans>Enter code</Trans>
                </button>
                <button
                  className="text-sm text-muted-foreground hover:text-foreground underline cursor-pointer"
                  onClick={() => setUIState("options")}
                >
                  <Trans>Sign in with password</Trans>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {uiState === "forgot" && (
        <div className="absolute inset-0 bg-background flex items-center justify-center z-50">
          {/* Back Button - Absolutely positioned top-left */}
          <button
            className="absolute top-6 left-8 flex items-center text-placeholder-foreground hover:text-foreground transition-colors z-10"
            onClick={() => setUIState("options")}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            <span className="text-sm"><Trans>Back</Trans></span>
          </button>

          <div className="flex flex-col justify-center items-center w-full max-w-[400px] gap-10 px-8">
            {/* Header */}
            <div className="text-center flex flex-col gap-2">
              <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground">
                <Trans>Reset password</Trans>
              </h2>
              <p className="text-base leading-7 text-muted-foreground">
                <Trans>We'll send you a link to reset your password</Trans>
              </p>
            </div>

            {/* Form */}
            <div className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-1.5 w-full">
                <label
                  htmlFor="email"
                  className="text-sm font-semibold leading-5 text-foreground"
                >
                  <Trans>Email</Trans>
                </label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  placeholder={t`Enter your email to reset your password`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (error) setError(false);
                    if (e.code === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmitForgot(e);
                    }
                  }}
                  ref={emailRef}
                  className="h-10 placeholder:text-placeholder-foreground"
                />
              </div>
              <Button
                className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
                type="submit"
                onClick={async (e) => handleSubmitForgot(e)}
                disabled={!email}
              >
                <Trans>Send link</Trans>
              </Button>
            </div>
          </div>
        </div>
      )}

      {uiState === "validate-forgot-password" && (
        <div className="absolute inset-0 bg-background z-50">
          {/* Back Button - Absolutely positioned top-left */}
          <button
            className="absolute top-6 left-8 flex items-center text-placeholder-foreground hover:text-foreground transition-colors z-10"
            onClick={() => setUIState("forgot")}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            <span className="text-sm"><Trans>Back</Trans></span>
          </button>

          <div className="h-full w-full flex items-center justify-center px-8">
            <div className="w-full max-w-[400px]">
              <ForgotPassword
                email={email}
                validatePin={validatePin}
                resendPIN={resendPIN}
                submitReset={submitReset}
                goBack={() => setUIState("forgot")}
                setLoading={setLoading}
              />
            </div>
          </div>
        </div>
      )}

      {uiState === "link" && (
        <div className="absolute inset-0 bg-background flex items-center justify-center z-50">
          {/* Back Button - Absolutely positioned top-left */}
          <button
            className="absolute top-6 left-8 flex items-center text-placeholder-foreground hover:text-foreground transition-colors z-10"
            onClick={() => setUIState("options")}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            <span className="text-sm"><Trans>Back</Trans></span>
          </button>

          <div className="flex flex-col justify-center items-center w-full max-w-[400px] gap-10 px-8">
            {/* Header */}
            <div className="text-center flex flex-col gap-2">
              <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground">
                <Trans>Sign in with a code</Trans>
              </h2>
              <p className="text-base leading-7 text-muted-foreground">
                <Trans>We'll email you a one-time code to sign in without a password</Trans>
              </p>
            </div>

            {/* Form */}
            <Form
              method="POST"
              action={`/login?index${safeSearchParams ? `&${safeSearchParams}` : ""}`}
              target="_self"
              className="w-full flex flex-col gap-6"
              onSubmit={() => {
                setLoading(true);
                toast.success(t`Code sent`);
              }}
            >
              <input type="hidden" name="intent" value="request-login-pin" />
              <input
                type="hidden"
                name="state"
                value={invalidInviteEmail ? "" : redirectUrl}
              />
              <input type="hidden" name="inviteCode" value={inviteCode} />

              <div className="flex flex-col gap-1.5 w-full">
                <label
                  htmlFor="email"
                  className="text-sm font-semibold leading-5 text-foreground"
                >
                  <Trans>Email</Trans>
                </label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  placeholder={t`Enter your email to receive a login code`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  ref={emailRef}
                  className="h-10 placeholder:text-placeholder-foreground"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
              >
                <Trans>Send code</Trans>
              </Button>
            </Form>
          </div>
        </div>
      )}
      {uiState === "enter-pin" && (
        <div className="absolute inset-0 bg-background flex items-center justify-center z-50">
          {/* Back Button - Absolutely positioned top-left */}
          <button
            className="absolute top-6 left-8 flex items-center text-placeholder-foreground hover:text-foreground transition-colors z-10"
            onClick={() => setUIState("options")}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            <span className="text-sm"><Trans>Back</Trans></span>
          </button>

          <div className="flex flex-col justify-center items-center w-full max-w-[400px] gap-10 px-8">
            {/* Header */}
            <div className="text-center flex flex-col gap-2">
              <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground">
                <Trans>Enter code</Trans>
              </h2>
              <p className="text-base leading-7 text-muted-foreground">
                <Trans>Check your email and enter the code</Trans>
              </p>
            </div>

            {/* Form */}
            <Form
              method="POST"
              action={`/login?index${safeSearchParams ? `&${safeSearchParams}` : ""}`}
              target="_self"
              className="w-full flex flex-col gap-6"
              onSubmit={() => setLoading(true)}
            >
              <input type="hidden" name="intent" value="login-with-pin" />
              <input
                type="hidden"
                name="state"
                value={invalidInviteEmail ? "" : redirectUrl}
              />
              <input type="hidden" name="inviteCode" value={inviteCode} />
              <input type="hidden" name="email" value={email} />

              <div className="flex flex-col gap-1.5 w-full">
                <label
                  htmlFor="pin"
                  className="text-sm font-semibold leading-5 text-foreground"
                >
                  <Trans>Code</Trans>
                </label>
                <Input
                  type="text"
                  name="pin"
                  id="pin"
                  autoComplete="one-time-code"
                  aria-invalid={error || undefined}
                  aria-describedby={error ? "pin-error" : undefined}
                  placeholder={t`Enter your code`}
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="h-10 placeholder:text-placeholder-foreground"
                />
              </div>

              {error && (
                <p id="pin-error" role="alert" className="text-sm text-destructive">
                  <Trans>Incorrect code! Please try again.</Trans>
                </p>
              )}

              <Button
                type="submit"
                className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
                disabled={!pin || !email}
              >
                <Trans>Sign in</Trans>
              </Button>
            </Form>

            <div className="text-sm text-muted-foreground">
              <Form
                method="POST"
                action={`/login?index${safeSearchParams ? `&${safeSearchParams}` : ""}`}
                target="_self"
                className="inline"
              >
                <input type="hidden" name="intent" value="request-login-pin" />
                <input type="hidden" name="state" value={redirectUrl} />
                <input type="hidden" name="email" value={email} />
                <input type="hidden" name="inviteCode" value={inviteCode} />
                <button
                  type="submit"
                  className="text-primary-hover underline hover:no-underline font-semibold"
                  onClick={() => {
                    toast.success(t`Code resent`);
                  }}
                >
                  <Trans>Resend code</Trans>
                </button>
              </Form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LoginEmail;
